import React, { useState, useMemo, useRef } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import Plot, { type PlotParams } from "react-plotly.js";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import type { Theme } from "src/theme";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import MuiCard from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import { type GridFilterModel } from "@material-ui/x-grid";

import {
  type TimeOption,
  getPeriodFromPreset,
} from "src/components/TimeFilter";
import TableHeader, { Title } from "src/components/TableHeader";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import { timeOptionToLocationState } from "src/utils/timeFilter";
import {
  ThemedSvgGradientDef,
  useChartColors,
  makeBarFill,
  makePieFill,
} from "src/components/SvgGradientDef";
import InfoTooltip from "src/components/InfoTooltip";
import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import {
  createTableToolbar,
  DefaultFeatures,
} from "src/components/TableToolbar";
import { salesAnalyticsSelector } from "src/store/dashboard/selector";
import {
  effectiveUserTimeZoneSelector,
  userGetInventoryTypeSelector,
} from "src/store/system/selector";
import { formatDollars } from "src/utils/formatter";
import {
  Columns,
  ColumnsCash,
  ReturnColumns,
  ReturnColumnsCash,
} from "src/pages/TransactionDetails/Sales/columns";
import { useRecordEvent } from "src/hooks/useRecordEvent";

import Card from "./Card";

const FieldOptions = [
  { field: "department", name: "Department" },
  { field: "category", name: "Category" },
  { field: "sub_category", name: "Sub-Category" },
  { field: "brand", name: "Brand" },
];

const SalesTableToolbar = createTableToolbar({
  features: {
    ...DefaultFeatures,
    QuickFilter: false,
  },
  options: { fileName: "My Reseller Genie - Sales" },
});
const ReturnsTableToolbar = createTableToolbar({
  features: {
    ...DefaultFeatures,
    QuickFilter: false,
  },
  options: { fileName: "My Reseller Genie - Returns" },
});

const useStyles = makeStyles((theme: Theme) => {
  const { custom: palette, derived } = theme.palette.custom;
  const grey = theme.palette.grey[400];

  const gradients = {
    Green: { name: "Green", color: palette.brandSecondary, stops: [] },
    LightGreen: {
      name: "LightGreen",
      color: derived.brandSecondaryLight,
      stops: [],
    },
    Blue: { name: "Blue", color: palette.brandPrimary, stops: [] },
    LightBlue: {
      name: "LightBlue",
      color: derived.brandPrimaryLight,
      stops: [],
    },
    Accent: { name: "Accent", color: palette.accent, stops: [] },
    Grey: { name: "Grey", color: grey, stops: [] },
    LightGrey: { name: "LightGrey", color: grey, stops: [] },
  };

  return {
    container: {
      display: "flex",
      marginBottom: theme.spacing(2),
      alignItems: "stretch",
    },
    metricCards: {
      display: "grid",
      gridAutoFlow: "column",
      gridAutoColumns: "1fr",
      gap: theme.spacing(2),
      "& > *": {
        minWidth: "max-content",
        whiteSpace: "nowrap",
      },
    },
    advancedFilteringCard: {
      alignSelf: "flex-start",
    },
    advancedFilteringCardContent: {
      padding: `${theme.spacing(1, 1, 1, 2)}!important`,
    },
    fieldSelect: {
      width: 250,
    },
    plot: {
      "& g.cartesianlayer g.plot g.barlayer.mlayer g.trace.bars g.points g.point path":
        {
          clipPath: "inset(0% 0% 0% 0% round 8px 8px 0px 0px)",
        },
    },
    departmentPlot: {
      ...makeBarFill([gradients.Green]),
    },
    categoryPlot: {
      ...makeBarFill([gradients.Blue]),
    },
    subCategoryPlot: {
      ...makeBarFill([gradients.LightBlue]),
    },
    brandPlot: {
      ...makeBarFill([gradients.Accent]),
    },
    horizontalPlot: {
      "& g.cartesianlayer g.plot g.barlayer.mlayer g.trace.bars g.points g.point path":
        {
          clipPath: "inset(0% 0% 0% 0% round 0px 8px 8px 0px)",
        },
      ...makeBarFill([gradients.Blue, gradients.LightGreen]),
    },
    piePlot: {
      ...makePieFill([
        gradients.Green,
        gradients.LightGreen,
        gradients.Blue,
        gradients.LightBlue,
        gradients.Grey,
        gradients.LightGrey,
      ]),
    },
    highlightedRow: {
      background: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
      "& .MuiDataGrid-cell": {
        color: theme.palette.warning.contrastText,
      },
      "& .MuiCheckbox-root": {
        color: theme.palette.warning.contrastText,
      },
      "&:hover, &.Mui-hovered": {
        background: `${theme.palette.warning.dark} !important`,
      },
    },
  };
});

export interface SalesAnalyticsProps {
  timeFilter: TimeOption;
}

function SalesAnalytics({ timeFilter }: SalesAnalyticsProps) {
  const recordEvent = useRecordEvent();

  const classes = useStyles();
  const theme = useTheme();
  const chartColors = useChartColors();
  const history = useHistory();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const salesDetailContainerRef = useRef<HTMLDivElement>(null);
  const [advancedFiltering, setAdvancedFiltering] = useState(false);
  const [fieldIndex, setFieldIndex] = useState(0);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();

  const periodDates = useMemo(() => {
    const period =
      timeFilter.type === "custom"
        ? timeFilter.period
        : getPeriodFromPreset(timeFilter, timeZone);
    return { start: period.start, end: period.end };
  }, [timeFilter, timeZone]);

  const period = useMemo(
    () => ({
      start: calendarYmdInTimeZone(periodDates.start, timeZone),
      end: calendarYmdInTimeZone(periodDates.end, timeZone),
    }),
    [periodDates, timeZone]
  );

  const handleSalesCardClick = (
    card: "gross_profit" | "average_sale_price"
  ) => {
    recordEvent({
      type: "feature-adoption",
      feature: "dashboard-card-click",
      card,
      timeFilter: timeFilter.type,
    });
    history.push("/transaction-details/sales", {
      ...timeOptionToLocationState(timeFilter, "no-refund-attached"),
    });
  };
  const {
    grossProfit,
    totalSalePrice,
    averageSalePrice,
    sellThroughRate,
    departments,
    categories,
    subcategories,
    brands,
    vendors,
    platforms,
    salesByListedDateBuckets,
    sales,
    refunds,
  } = useSelector((state) =>
    salesAnalyticsSelector(state, {
      period,
      filter: advancedFiltering ? filterModel : undefined,
    })
  );
  const inventoryTypeIsCash =
    useSelector(userGetInventoryTypeSelector) === "cash";
  const salesTableColumns = inventoryTypeIsCash ? ColumnsCash : Columns;

  const profitMarginPercents = vendors.salePrice.map((salePrice, i) => {
    if (!salePrice || salePrice === 0) return "0%";
    const percent = ((vendors.grossProfit[i] / salePrice) * 100).toFixed(0);
    return `${percent}%  `;
  });

  const grossProfitByDaysListedData = useMemo(() => {
    const pairs = salesByListedDateBuckets.labels.map((label, i) => ({
      label,
      value: salesByListedDateBuckets.grossProfit[i],
    }));
    pairs.sort((a, b) => b.value - a.value);

    // Filter out negative values to prevent pie chart rendering issues
    // Plotly calculates percentages using sum of all values (including negatives)
    // but renders slice sizes using absolute values, causing a mismatch
    const filteredPairs = pairs.filter((profit) => profit.value > 0);

    return [
      {
        labels: filteredPairs.map((profit) => profit.label),
        values: filteredPairs.map((profit) => profit.value),
        type: "pie",
        marker: {
          colors: [
            chartColors.Green,
            chartColors.LightGreen,
            chartColors.Blue,
            chartColors.LightBlue,
            chartColors.Grey,
            chartColors.LightGrey,
          ],
        },
        hole: 0.4,
        hovertemplate:
          "%{label}<br />%{value:$,.2f}<br />%{percent}<extra></extra>",
      },
    ] as PlotParams["data"];
  }, [salesByListedDateBuckets]);

  const grossProfitByPlatformData = useMemo(() => {
    const pairs = platforms.labels.map((label, i) => ({
      label,
      value: platforms.values[i],
    }));
    pairs.sort((a, b) => b.value - a.value);

    // Filter out negative values to prevent pie chart rendering issues
    // Plotly calculates percentages using sum of all values (including negatives)
    // but renders slice sizes using absolute values, causing a mismatch
    const filteredPairs = pairs.filter((profit) => profit.value > 0);

    return [
      {
        labels: filteredPairs.map((profit) => profit.label),
        values: filteredPairs.map((profit) => profit.value),
        type: "pie",
        marker: {
          colors: [
            chartColors.Green,
            chartColors.LightGreen,
            chartColors.Blue,
            chartColors.LightBlue,
            chartColors.Grey,
            chartColors.LightGrey,
          ],
        },
        hole: 0.4,
        hovertemplate:
          "%{label}<br />%{value:$,.2f}<br />%{percent}<extra></extra>",
      },
    ] as PlotParams["data"];
  }, [platforms]);

  return (
    <Box>
      <div className={classes.container}>
        <div className={classes.metricCards}>
          <Card
            title="Gross Profit"
            content={
              <>
                <span>{formatDollars(grossProfit)}</span>
                <InfoTooltip
                  disableButton
                  size="small"
                  text="The gross profit includes the gross profit of each sale displayed in the 'Sales Detail' except for sales that have a refund attached to them."
                />
              </>
            }
            onClick={() => handleSalesCardClick("gross_profit")}
          />
          <Card
            title="Average Sale Price"
            content={
              <>
                <span>{formatDollars(averageSalePrice)}</span>
                <InfoTooltip
                  size="small"
                  text="The average sale price is the total of sale prices/number of sales displayed in the 'Sales Detail' that do not have a refund attached to them."
                  disableButton
                />
              </>
            }
            onClick={() => handleSalesCardClick("average_sale_price")}
          />
          {!inventoryTypeIsCash && (
            <Card
              title="Sell Through Rate"
              content={
                <>
                  {advancedFiltering && filterModel ? (
                    <span style={{ color: theme.palette.error.main }}>--%</span>
                  ) : (
                    <span>{`${sellThroughRate} %`}</span>
                  )}
                  <InfoTooltip
                    disableButton
                    size="small"
                    color={
                      advancedFiltering && filterModel ? "error" : undefined
                    }
                    text={
                      advancedFiltering && filterModel
                        ? "Sell Through Rate cannot be calculated accurately when filtering sales."
                        : "Sell Through Rate is a key performance indicator (KPI) for inventory-based businesses. It tells you how much inventory you moved during a period compared to how much you purchased. This is calculated as # of items sold/# of items purchased during a period of time."
                    }
                  />
                </>
              }
            />
          )}
        </div>
        <Box flex={1} />
        <MuiCard className={classes.advancedFilteringCard}>
          <CardContent
            className={classes.advancedFilteringCardContent}
            id={"advanced-filtering-card"}
          >
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={advancedFiltering}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    recordEvent({
                      type: "feature-adoption",
                      feature: "sales-analytics-advanced-filtering",
                      checked,
                    });
                    setAdvancedFiltering(checked);
                    if (!checked) return;
                    // we need to wait for the div to appear
                    const f = (count = 10) => {
                      if (count < 0) return;
                      if (salesDetailContainerRef.current) {
                        salesDetailContainerRef.current.scrollIntoView({
                          behavior: "smooth",
                        });
                        return;
                      }
                      setTimeout(() => f(count - 1), 100);
                    };
                    f();
                  }}
                  name="advanced-filtering"
                />
              }
              label="Advanced Filtering"
            />
          </CardContent>
        </MuiCard>
      </div>
      <ThemedSvgGradientDef />
      <Box
        component={Paper}
        display="flex"
        flexDirection="column"
        height="auto"
        p={2.5}
        mb={2}
      >
        <TableHeader
          title={
            <>
              <span>Profit and Sales Detail</span>
              <InfoTooltip text="This chart shows two pieces of information. The green bar shows the sum of the gross profit. The white line shows the number of sales. Each of these is divided based on 'Department', and you can see more detail by changing the dropdown to 'Category' or 'Sub-Category'." />
            </>
          }
        >
          <Select
            className={classes.fieldSelect}
            value={fieldIndex}
            onChange={(e) =>
              setFieldIndex(parseInt(e.target.value as string, 10))
            }
            variant="outlined"
            margin="dense"
          >
            {FieldOptions.map((e, i) => (
              <MenuItem value={i} key={i}>
                {e.name}
              </MenuItem>
            ))}
          </Select>
        </TableHeader>
        <Plot
          className={clsx(
            classes.plot,
            fieldIndex === 0 && classes.departmentPlot,
            fieldIndex === 1 && classes.categoryPlot,
            fieldIndex === 2 && classes.subCategoryPlot,
            fieldIndex === 3 && classes.brandPlot
          )}
          data={
            fieldIndex === 0
              ? [
                  {
                    x: departments.keys,
                    y: departments.grossProfit,
                    type: "bar",
                    marker: { color: chartColors.Green },
                    name: "Sum of Gross Profit",
                    hovertemplate:
                      "Sum of Gross Profit<br />%{x}: %{y:$,.2f}<extra></extra>",
                  },
                  {
                    x: departments.keys,
                    y: departments.count,
                    type: "scatter",
                    mode: "lines+markers",
                    line: { shape: "spline" },
                    marker: { color: theme.palette.grey[400] },
                    hovertemplate:
                      "Count of Sales<br />%{x}: %{y}<extra></extra>",
                    name: "Count of Sales",
                    yaxis: "y2",
                  },
                ]
              : fieldIndex === 1
              ? [
                  {
                    x: categories.keys,
                    y: categories.grossProfit,
                    type: "bar",
                    marker: { color: chartColors.Blue },
                    name: "Sum of Gross Profit",
                    hovertemplate:
                      "Sum of Gross Profit<br />%{x}: %{y:$,.2f}<extra></extra>",
                  },
                  {
                    x: categories.keys,
                    y: categories.count,
                    type: "scatter",
                    mode: "lines",
                    line: { shape: "spline" },
                    marker: { color: theme.palette.grey[400] },
                    hovertemplate:
                      "Count of Sales<br />%{x}: %{y}<extra></extra>",
                    name: "Count of Sales",
                    yaxis: "y2",
                  },
                ]
              : fieldIndex === 2
              ? [
                  {
                    x: subcategories.keys,
                    y: subcategories.grossProfit,
                    type: "bar",
                    marker: { color: chartColors.LightBlue },
                    hovertemplate:
                      "Sum of Gross Profit<br />%{x}: %{y:$,.2f}<extra></extra>",
                    name: "Sum of Gross Profit",
                  },
                  {
                    x: subcategories.keys,
                    y: subcategories.count,
                    type: "scatter",
                    mode: "lines",
                    line: { shape: "spline" },
                    marker: { color: theme.palette.grey[400] },
                    hovertemplate:
                      "Count of Sales<br />%{x}: %{y}<extra></extra>",
                    name: "Count of Sales",
                    yaxis: "y2",
                  },
                ]
              : fieldIndex === 3
              ? [
                  {
                    x: brands.keys,
                    y: brands.grossProfit,
                    type: "bar",
                    marker: { color: chartColors.Accent },
                    name: "Sum of Gross Profit",
                    hovertemplate:
                      "Sum of Gross Profit<br />%{x}: %{y:$,.2f}<extra></extra>",
                  },
                  {
                    x: brands.keys,
                    y: brands.count,
                    type: "scatter",
                    mode: "lines+markers",
                    line: { shape: "spline" },
                    marker: { color: theme.palette.grey[400] },
                    hovertemplate:
                      "Count of Sales<br />%{x}: %{y}<extra></extra>",
                    name: "Count of Sales",
                    yaxis: "y2",
                  },
                ]
              : []
          }
          config={{
            displaylogo: false,
            displayModeBar: false,
            responsive: true,
          }}
          layout={{
            autosize: true,
            height: 450,
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: {
              color: theme.palette.text.primary,
            },
            legend: {
              orientation: "h",
              xanchor: "center",
              x: 0.5,
              y: 1.1,
            },
            xaxis: {
              showgrid: false,
              zerolinecolor: theme.palette.divider,
            },
            yaxis: {
              tickprefix: "$",
              gridcolor: theme.palette.divider,
              zerolinecolor: theme.palette.divider,
            },
            yaxis2: {
              overlaying: "y",
              side: "right",
              showgrid: false,
              zerolinecolor: theme.palette.divider,
            },
          }}
        />
      </Box>
      <Grid container spacing={2}>
        <Grid item sm={12} md={6}>
          <Box
            component={Paper}
            display="flex"
            flexDirection="column"
            height="auto"
            p={2.5}
            mb={2}
          >
            <Title>
              Gross Profit by Days Listed
              <InfoTooltip
                text={
                  <>
                    This chart groups your gross profit by the number of days an
                    item sits before selling. This allows you to see how fast
                    your inventory is selling.
                    <br /> <br />
                    Note: Periods with a negative gross profit are excluded in
                    the chart.
                  </>
                }
              />
            </Title>
            <Plot
              className={classes.piePlot}
              data={grossProfitByDaysListedData}
              config={{
                displaylogo: false,
                displayModeBar: false,
                responsive: true,
              }}
              layout={{
                autosize: true,
                height: 450,
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                font: {
                  color: theme.palette.text.primary,
                },
                xaxis: {
                  gridcolor: theme.palette.divider,
                  zerolinecolor: theme.palette.divider,
                },
                yaxis: {
                  tickprefix: "$",
                  gridcolor: theme.palette.divider,
                  zerolinecolor: theme.palette.divider,
                },
              }}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={6}>
          <Box
            component={Paper}
            display="flex"
            flexDirection="column"
            height="auto"
            p={2.5}
            mb={2}
          >
            <Title>
              Gross Profit by Platform
              <InfoTooltip
                text={
                  <>
                    This chart groups your gross profit by the sales platform,
                    so you can see where you make the most profit.
                    <br /> <br />
                    Note: Sale platforms with a negative gross profit are
                    excluded from the chart. Platforms that account for less
                    than 5% of your total gross profit are grouped into "Other".
                  </>
                }
              />
            </Title>
            <Plot
              className={classes.piePlot}
              data={grossProfitByPlatformData}
              config={{
                displaylogo: false,
                displayModeBar: false,
                responsive: true,
              }}
              layout={{
                autosize: true,
                height: 450,
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                font: {
                  color: theme.palette.text.primary,
                },
                xaxis: {
                  gridcolor: theme.palette.divider,
                  zerolinecolor: theme.palette.divider,
                },
                yaxis: {
                  tickprefix: "$",
                  gridcolor: theme.palette.divider,
                  zerolinecolor: theme.palette.divider,
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>
      <Box
        component={Paper}
        display="flex"
        flexDirection="column"
        height="auto"
        p={2.5}
        mb={2}
      >
        <TableHeader
          title={
            <>
              <span>Revenue vs Profit by Source</span>
              <InfoTooltip
                text="This chart compares your total revenue (blue) against your
              profit (green) for each source where you purchased inventory (the source is the vendor field). Profit is
              calculated as: Sale Price - Purchase Price - Transaction Fees - Shipping Cost - Other Fees."
              />
            </>
          }
        />
        <Plot
          className={classes.horizontalPlot}
          data={[
            {
              y: vendors.keys,
              x: vendors.salePrice,
              type: "bar",
              orientation: "h",
              name: "Revenue",
              marker: { color: chartColors.Blue },
              hovertemplate: "Revenue<br />%{y}: %{x:$,.2f}<extra></extra>",
              width: 0.4,
            },
            {
              y: vendors.keys,
              x: vendors.grossProfit,
              type: "bar",
              orientation: "h",
              name: "Profit",
              marker: { color: chartColors.LightGreen },
              hovertemplate: "Profit<br />%{y}: %{x:$,.2f}<extra></extra>",
              width: 0.4,
              text: profitMarginPercents,
              textposition: "inside",
              insidetextanchor: "end",
              textfont: {
                color: chartColors.onLightGreen,
                size: 12,
              },
            },
          ]}
          config={{
            displaylogo: false,
            displayModeBar: false,
            responsive: true,
          }}
          layout={{
            autosize: true,
            height: Math.max(300, vendors.keys.length * 50 + 100),
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: {
              color: theme.palette.text.primary,
            },
            barmode: "overlay",
            legend: {
              orientation: "h",
              xanchor: "center",
              x: 0.5,
              y: 1.1,
            },
            margin: { l: 120, r: 40, t: 40, b: 40 },
            xaxis: {
              tickprefix: "$",
              gridcolor: theme.palette.divider,
              zerolinecolor: theme.palette.divider,
            },
            yaxis: {
              automargin: true,
              ticksuffix: "  ",
              showgrid: false,
              zerolinecolor: theme.palette.divider,
            },
          }}
        />
      </Box>
      <Box
        component={Paper}
        display="flex"
        flexDirection="column"
        height="auto"
        p={2.5}
        mb={2}
      >
        <Title>
          Total Sales by Days Listed
          <InfoTooltip text="This chart shows two pieces of information. The blue line shows the average sales price. The other line shows the number of sales. Both are divided based on the number of days the item was listed before selling." />
        </Title>
        <Plot
          data={[
            {
              x: salesByListedDateBuckets.labels,
              y: salesByListedDateBuckets.count,
              name: "Total Sales",
              type: "scatter",
              mode: "lines+markers",
              line: { shape: "spline" },
              marker: { color: theme.palette.primary.main },
              hovertemplate: "Total Sales<br />%{x}: %{y}<extra></extra>",
            },
            {
              x: salesByListedDateBuckets.labels,
              y: salesByListedDateBuckets.average,
              name: "Average Sale Price",
              type: "scatter",
              mode: "lines+markers",
              line: { shape: "spline" },
              marker: {
                color:
                  theme.palette.type === "dark"
                    ? chartColors.brandPrimaryLight
                    : chartColors.brandPrimary,
              },
              hovertemplate:
                "Average Sale Price<br />%{x}: %{y:,.2f}<extra></extra>",
              yaxis: "y2",
            },
          ]}
          config={{
            displaylogo: false,
            displayModeBar: false,
            responsive: true,
          }}
          layout={{
            autosize: true,
            height: 450,
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: {
              color: theme.palette.text.primary,
            },
            legend: {
              orientation: "h",
              xanchor: "center",
              x: 0.5,
              yanchor: "top",
              y: -0.1,
            },
            xaxis: {
              gridcolor: theme.palette.divider,
              zerolinecolor: theme.palette.divider,
            },
            yaxis: {
              gridcolor: theme.palette.divider,
              zerolinecolor: theme.palette.divider,
            },
            yaxis2: {
              tickprefix: "$",
              overlaying: "y",
              side: "right",
              showgrid: false,
              zerolinecolor: theme.palette.divider,
            },
          }}
        />
      </Box>
      {advancedFiltering && (
        <>
          <div ref={salesDetailContainerRef} />
          <Box
            component={Paper}
            display="flex"
            flexDirection="column"
            height="700px"
            p={2.5}
            mb={2}
          >
            <TableHeader title="Sales Detail" />
            <XGrid
              checkboxSelection
              tableName="sales"
              Component={CSVFormattedXGrid}
              rows={sales}
              columns={salesTableColumns}
              headerHeight={35}
              rowHeight={35}
              components={{
                Toolbar: SalesTableToolbar,
              }}
              onFilterModelChange={(model) => {
                recordEvent({
                  type: "feature-adoption",
                  feature: "sales-analytics-table-filter",
                  element: "sales",
                  filter: JSON.stringify(model),
                });
                if (model.items.length === 1 && !("value" in model.items[0]))
                  setFilterModel(undefined);
                else setFilterModel(model);
              }}
              filterModel={filterModel}
              filterMode="server"
              getRowClassName={({ row }) => {
                if (row.in_process) {
                  return classes.highlightedRow;
                }
                return "";
              }}
            />
          </Box>
          <Box
            component={Paper}
            display="flex"
            flexDirection="column"
            height="700px"
            p={2.5}
            mb={2}
          >
            <TableHeader title="Refunds Detail" />
            <XGrid
              checkboxSelection
              checkboxSelectionVisibleOnly
              tableName="returns"
              Component={CSVFormattedXGrid}
              rows={refunds}
              columns={inventoryTypeIsCash ? ReturnColumnsCash : ReturnColumns}
              headerHeight={35}
              rowHeight={35}
              components={{
                Toolbar: ReturnsTableToolbar,
              }}
              onFilterModelChange={(model) => {
                recordEvent({
                  type: "feature-adoption",
                  feature: "sales-analytics-table-filter",
                  element: "refunds",
                  filter: JSON.stringify(model),
                });
                if (model.items.length === 1 && !("value" in model.items[0]))
                  setFilterModel(undefined);
                else setFilterModel(model);
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default SalesAnalytics;
